import { z } from 'zod';
import type { ParticipantRole } from '../registries/roles.ts';

/**
 * LiveKit participant identities. The prefix tells which subject the identity belongs to, so the
 * server can resolve it without a lookup table.
 */
export const IDENTITY_PREFIXES = { account: 'acc_', guest: 'gst_' } as const;
export type IdentitySubjectKind = keyof typeof IDENTITY_PREFIXES;

export const identityFor = (kind: IdentitySubjectKind, subjectId: string): string =>
  `${IDENTITY_PREFIXES[kind]}${subjectId}`;

export const parseIdentity = (identity: string): { kind: IdentitySubjectKind; subjectId: string } | null => {
  for (const [kind, prefix] of Object.entries(IDENTITY_PREFIXES) as [IdentitySubjectKind, string][]) {
    if (identity.startsWith(prefix)) return { kind, subjectId: identity.slice(prefix.length) };
  }
  return null;
};

/** Participant attribute keys. `role` is set by the server in the token; `sync` by each client. */
export const PARTICIPANT_ATTRIBUTES = {
  role: 'seans.role',
  sync: 'seans.sync',
} as const;

export const SYNC_STATUSES = ['idle', 'synced', 'catching_up', 'buffering'] as const;
export const SyncStatus = z.enum(SYNC_STATUSES);
export type SyncStatus = z.infer<typeof SyncStatus>;

export const roleFromAttributes = (attributes: Readonly<Record<string, string>>): ParticipantRole =>
  attributes[PARTICIPANT_ATTRIBUTES.role] === 'host' ? 'host' : 'guest';

/** LiveKit room name for a screening. */
export const roomNameFor = (screeningId: string): string => `scr_${screeningId}`;
